import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type AllocActivityKind } from '../lib/api';
import { kindLabel } from '../lib/driverKind';
import { useSticky } from '../lib/sticky';
import AllocHistoryPanel from './AllocHistoryPanel';
import { ErrorBox, dash, money, wallClock, type Row } from './DataTable';

/**
 * 운행차량편성 아래 내역 탭(#300 유상은 · 구 버스인 운행차량편성 아래 7탭) — 기준정보에서 편성 줄을 **한 번** 누르면 그 편성의
 * 배차기록 · 재무추가 · 월정기 · 영업정산 · 기사운행료 · 할부금 · 편성로그를 표 아래에서 본다(두 번 누르면 수정 창 — 그대로).
 *
 * 높이는 고정(`data-fit-below` — 위 표가 이만큼을 빼고 화면에 맞춘다 · lib/fitHeight.ts)이고 탭 안 표만 움직인다.
 * 금액 탭은 서버가 원래 화면 권한을 보고 없으면 `hidden` 을 준다(탭 하나 못 보는 게 화면 오류가 되지 않게).
 */

type Tab = AllocActivityKind | 'log';
const TABS: { id: Tab; label: string }[] = [
  { id: 'dispatch', label: '배차기록' },
  { id: 'daily', label: '재무추가' },
  { id: 'mo', label: '월정기' },
  { id: 'req', label: '영업정산' },
  { id: 'drive', label: '기사운행료' },
  { id: 'inst', label: '할부금' },
  { id: 'log', label: '편성로그' },
];
const isTab = (v: unknown): v is Tab => TABS.some((t) => t.id === v);

type Res = Awaited<ReturnType<typeof api.listAllocActivity>>;

/** 원 단위 합(문자열 금액 — 표시용. 서버 합이 있으면 그걸 쓴다). */
const sumOf = (rows: Row[], key: string) => rows.reduce((s, r) => s + (Number(r[key]) || 0), 0);
const mon = (v: unknown) => (v === '' ? '이월잔액' : dash(v));
const io = (v: unknown) => (v === true ? <span className="text-brand-700">지급</span> : v === false ? <span className="text-red-600">공제</span> : '—');
const who = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v).split('@')[0]);
const day = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v).slice(0, 10));

export default function AllocActivityPanel({ alloc }: { alloc: Row | null }) {
  const id = alloc ? Number(alloc.car_alloc_id) : 0;
  const [tab, setTab] = useSticky<Tab>('master.alloc-activity.tab', 'dispatch', { persist: true, valid: isTab });
  // 접어 두면 머리 줄만 — 표가 그만큼 길어진다(계정 기억).
  const [open, setOpen] = useSticky<boolean>('master.alloc-activity.open', true, { persist: true, valid: isBool });
  const [driveMon, setDriveMon] = useState<string>('');
  const [res, setRes] = useState<Res | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 편성이 바뀌면 고른 달은 버린다(그 편성이 탄 가장 최근 달부터).
  useEffect(() => { setDriveMon(''); }, [id]);

  useEffect(() => {
    if (!id || !open || tab === 'log') return;
    let gone = false;
    setRes(null);
    setError(null);
    api.listAllocActivity({ carAllocId: id, kind: tab, driveMon: tab === 'drive' && driveMon ? driveMon : undefined })
      .then((r) => { if (!gone) setRes(r); })
      .catch((e) => { if (!gone) setError(e instanceof Error ? e.message : String(e)); });
    return () => { gone = true; };
  }, [id, tab, driveMon, open]);

  return (
    // 늘 같은 높이로 둔다 — 줄을 고를 때마다 위 표 높이가 바뀌면 누른 줄이 밀려 가려진다.
    <section data-fit-below className="card flex flex-col overflow-hidden"
             style={open ? { height: 'clamp(14rem, 38vh, 28rem)' } : undefined}>
      <div className="flex flex-wrap items-center gap-1 border-b border-line px-2 py-1">
        <span className="mr-2 text-xs font-semibold text-ink">
          {alloc ? <>편성 NO {id} · {dash(alloc.car_no)} · {dash(alloc.driver_name)} · {dash(alloc.owner_name)}</> : '편성 내역'}
        </span>
        {open && TABS.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)}
                  className={`rounded px-2 py-0.5 text-xs font-medium ${t.id === tab ? 'bg-brand-600 text-white' : 'text-ink-soft hover:bg-canvas'}`}>
            {t.label}
          </button>
        ))}
        <button type="button" className="ml-auto rounded px-2 py-0.5 text-xs text-ink-soft hover:bg-canvas" onClick={() => setOpen(!open)}>
          {open ? '접기 ▾' : '펼치기 ▴'}
        </button>
      </div>
      {open && (
        <div className="min-h-0 flex-1 overflow-hidden">
          {!alloc ? (
            <p className="p-3 text-sm text-ink-soft">
              편성 줄을 한 번 누르면 이 아래에 배차기록·재무추가·월정기·영업정산·기사운행료·할부금·편성로그가 보입니다. 두 번 누르면 수정 창이 열립니다.
            </p>
          ) : tab === 'log' ? (
            <div className="h-full overflow-auto p-2"><AllocHistoryPanel carAllocId={id} reloadKey={0} /></div>
          ) : error ? (
            <div className="p-2"><ErrorBox message={error} /></div>
          ) : !res ? (
            <p className="p-3 text-xs text-ink-faint">불러오는 중…</p>
          ) : res.hidden ? (
            <p className="p-3 text-sm text-ink-soft">
              {res.reason === 'scope' ? '담당 범위 밖의 기사·차주라 이 내역을 볼 수 없습니다.' : '이 내역을 볼 권한이 없습니다.'}
            </p>
          ) : (
            <Body tab={tab} res={res} driveMon={driveMon} setDriveMon={setDriveMon} />
          )}
        </div>
      )}
    </section>
  );
}

const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

function Body({ tab, res, driveMon, setDriveMon }: {
  tab: AllocActivityKind; res: Res; driveMon: string; setDriveMon: (m: string) => void;
}) {
  const rows = (res.rows ?? []) as Row[];
  const total = (res.total ?? {}) as Record<string, unknown>;
  const count = Number(total.count ?? rows.length);
  const more = count > rows.length ? `전체 ${count.toLocaleString('ko-KR')}줄 중 최근 ${rows.length.toLocaleString('ko-KR')}줄` : null;

  switch (tab) {
    case 'dispatch':
      return (
        <Grid head={['정산월', '구분', '거래처', '노선명', '출발지', '도착지', '출발', '도착', '대수', '청구(노선)', '운행료(1대)', '접수', '노선']}
              note={more ?? '이 편성으로 배정된 노선(최근 순)'} empty="이 편성으로 배정된 노선이 없습니다.">
          {rows.map((r) => (
            <tr key={String(r.dispt_car_sch_id)}>
              <Td>{dash(r.drive_mon)}</Td><Td>{dash(r.recv_type)}</Td><Td clip>{dash(r.cust_name)}</Td><Td clip>{dash(r.dispt_name)}</Td>
              <Td clip>{dash(r.start_loc)}</Td><Td clip>{dash(r.end_loc)}</Td>
              <Td>{wallClock(r.start_time)}</Td><Td>{wallClock(r.end_time)}</Td>
              <Td num>{dash(r.car_cnt)}</Td><Td num>{money(r.cost_cust)}</Td><Td num>{money(r.cost_driver)}</Td>
              <Td num>{dash(r.recv_id)}</Td><Td num>{dash(r.dispt_id)}</Td>
            </tr>
          ))}
        </Grid>
      );

    case 'daily':
    case 'mo': {
      const amtKey = tab === 'daily' ? 'account_amt' : 'amt';
      const plus = Number(total.plus ?? 0);
      const minus = Number(total.minus ?? 0);
      const head = tab === 'daily'
        ? ['NO', '정산월', '구분', '계정', '금액', '발생일', '비고', '등록일', '등록자']
        : ['NO', '정산월', '구분', '계정', '금액', '비고', '등록일', '등록자'];
      return (
        <Grid head={head} note={more}
              empty={tab === 'daily' ? '이 편성의 재무추가 내역이 없습니다.' : '이 편성의 월정기 내역이 없습니다.'}
              foot={rows.length ? (
                <tr>
                  <Td colSpan={4} strong>합계 {count.toLocaleString('ko-KR')}줄 · 지급 {money(plus)} · 공제 {money(minus)}</Td>
                  <Td num strong>{money(plus - minus)}</Td>
                  <Td colSpan={head.length - 5}>지급 − 공제</Td>
                </tr>
              ) : null}>
          {rows.map((r) => (
            <tr key={String(r.result_daily_id ?? r.result_mo_id)}>
              <Td num>{dash(r.result_daily_id ?? r.result_mo_id)}</Td><Td>{mon(r.drive_mon)}</Td><Td>{io(r.is_plus)}</Td>
              <Td clip>{dash(r.account_name)}</Td><Td num>{money(r[amtKey])}</Td>
              {tab === 'daily' && <Td>{day(r.ex_date)}</Td>}
              <Td clip wide>{dash(r.comment)}</Td><Td>{day(r.created_at)}</Td><Td>{who(r.created_by)}</Td>
            </tr>
          ))}
        </Grid>
      );
    }

    case 'req':
      return (
        <Grid head={['NO', '정산월', '구분', '거래처', '노선명', '출발', '거래처 +', '거래처 −', '기사 지급', '기사 공제', '계정', '비고']}
              note={more} empty="이 차량·기사의 영업정산 내역이 없습니다."
              foot={rows.length ? (
                <tr>
                  <Td colSpan={6} strong>합계 {count.toLocaleString('ko-KR')}줄</Td>
                  <Td num strong>{money(total.cust_plus)}</Td><Td num strong>{money(total.cust_minus)}</Td>
                  <Td num strong>{money(total.driver_plus)}</Td><Td num strong>{money(total.driver_minus)}</Td>
                  <Td colSpan={2} />
                </tr>
              ) : null}>
          {rows.map((r) => (
            <tr key={String(r.result_req_id)}>
              <Td num>{dash(r.result_req_id)}</Td><Td>{dash(r.drive_mon)}</Td><Td>{dash(r.recv_type)}</Td>
              <Td clip>{dash(r.cust_name)}</Td><Td clip>{dash(r.dispt_name)}</Td><Td>{wallClock(r.start_time)}</Td>
              <Td num>{nz(r.cust_plus)}</Td><Td num>{nz(r.cust_minus)}</Td><Td num>{nz(r.driver_plus)}</Td><Td num>{nz(r.driver_minus)}</Td>
              <Td clip>{[r.cust_plus_account, r.cust_minus_account, r.driver_plus_account, r.driver_minus_account, r.company_cost_account]
                .filter((x) => x).join(' · ') || '—'}</Td>
              <Td clip wide>{dash(r.comment)}</Td>
            </tr>
          ))}
        </Grid>
      );

    case 'drive':
      return <DriveTab res={res} driveMon={driveMon} setDriveMon={setDriveMon} />;

    case 'inst':
      return <InstTab res={res} />;
  }
}

const nz = (v: unknown) => (v === null || v === undefined || v === '' || Number(v) === 0 ? '' : money(v));

/** 기사운행료 — 거래처정산에서 등록한 운행료(청구 상세와 같은 줄) · 기사+차량 묶음 소계. */
function DriveTab({ res, driveMon, setDriveMon }: { res: Res; driveMon: string; setDriveMon: (m: string) => void }) {
  const rows = (res.rows ?? []) as Row[];
  const months = res.months ?? [];
  const cur = driveMon || res.mon || '';
  // 한 편성이라 보통 한 묶음이지만, 구 버스인처럼 기사+차량으로 묶어 소계를 낸다.
  const groups = useMemo(() => {
    const m = new Map<string, Row[]>();
    for (const r of rows) {
      const k = `${String(r.driver_name ?? '')}|${String(r.car_no ?? '')}`;
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.values()];
  }, [rows]);
  const head = ['정산월', '거래처정산담당자', '고객명', '노선명', '접수구분', '차량번호', '출발지', '도착지', '기사명', '기사구분',
    '운행료단가', '운행수', '운행료합계', '운행시작', '운행종료', '연식'];
  const total = (res.total ?? {}) as Record<string, unknown>;
  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-line px-2 py-1 text-xs text-ink-soft">
        <span>정산년월</span>
        {months.length ? (
          <select className="field w-auto py-0.5 text-xs" value={cur} onChange={(e) => setDriveMon(e.target.value)} aria-label="정산년월">
            {!months.includes(cur) && cur && <option value={cur}>{cur}</option>}
            {months.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
        ) : (
          <input type="month" className="field w-auto py-0.5 text-xs" value={cur} onChange={(e) => setDriveMon(e.target.value)} aria-label="정산년월" />
        )}
        <span>거래처정산에서 등록한 기사운행료입니다(이 차량·기사 줄).</span>
      </div>
      <div className="min-h-0 flex-1">
        <Grid head={head} empty={`${cur} 에 이 차량·기사의 운행료 줄이 없습니다.`}
              foot={rows.length ? (
                <tr>
                  <Td colSpan={11} strong>합계</Td>
                  <Td num strong>{Number(total.cnt ?? 0).toLocaleString('ko-KR')}</Td>
                  <Td num strong>{money(total.amt)}</Td>
                  <Td colSpan={3} />
                </tr>
              ) : null}>
          {groups.flatMap((g, gi) => [
            ...g.map((r, i) => (
              <tr key={`${gi}-${i}`}>
                <Td>{dash(r.drive_mon)}</Td><Td>{dash(r.fin_manager)}</Td><Td clip>{dash(r.cust_name)}</Td><Td clip>{dash(r.dispt_name)}</Td>
                <Td>{dash(r.recv_type)}</Td><Td>{dash(r.car_no)}</Td><Td clip>{dash(r.start_loc)}</Td><Td clip>{dash(r.end_loc)}</Td>
                <Td>{dash(r.driver_name)}</Td>
                <Td>{kindLabel(r.driver_kind)}{r.direct === true && <span className="ml-1 text-[11px] text-amber-700" title="관리기사A 운행료는 지급계에서 빠집니다">(지급 제외)</span>}</Td>
                <Td num>{money(r.unit)}</Td><Td num>{dash(r.cnt)}</Td><Td num>{money(r.amt)}</Td>
                <Td>{wallClock(r.run_start)}</Td><Td>{wallClock(r.run_end)}</Td><Td>{dash(r.car_year)}</Td>
              </tr>
            )),
            <tr key={`s${gi}`} className="bg-canvas">
              <Td colSpan={11}>소계 · {dash(g[0]?.driver_name)} · {dash(g[0]?.car_no)}</Td>
              <Td num>{sumOf(g, 'cnt').toLocaleString('ko-KR')}</Td><Td num>{money(sumOf(g, 'amt'))}</Td><Td colSpan={3} />
            </tr>,
          ])}
        </Grid>
      </div>
    </div>
  );
}

const INST_STATE: Record<string, string> = { active: '진행', done: '끝남', repay: '상환' };

/** 할부금 — 계약(회차 요약) · 계약을 누르면 그 회차가 아래에. */
function InstTab({ res }: { res: Res }) {
  const rows = (res.rows ?? []) as Row[];
  const amts = (res.amts ?? []) as Row[];
  const [open, setOpen] = useState<number | null>(null);
  const picked = open ?? (rows.length === 1 ? Number(rows[0]!.fin_inst_id) : null);
  const lines = amts.filter((a) => Number(a.fin_inst_id) === picked);
  return (
    <div className="flex h-full flex-col">
      <div className={lines.length ? 'max-h-[45%] min-h-0' : 'min-h-0 flex-1'}>
        <Grid head={['NO', '캐피탈', '상태', '공제 시작', '회차', '남은 회차', '남은 금액', `이번 달(${dash(res.mon)})`, '월 할부금', '원금', '상환일', '비고']}
              note={rows.length > 1 ? '계약을 누르면 회차가 아래에 보입니다.' : null}
              empty="이 차량·차주의 할부 계약이 없습니다.">
          {rows.map((r) => {
            const fid = Number(r.fin_inst_id);
            return (
              <tr key={fid} onClick={() => setOpen(fid === picked ? -1 : fid)}
                  className={`cursor-pointer ${fid === picked ? 'bg-brand-50' : 'hover:bg-canvas'}`}>
                <Td num>{fid}</Td><Td clip>{dash(r.company)}</Td>
                <Td>{INST_STATE[String(r.state)] ?? dash(r.state)}{r.own_contract === false && <span className="ml-1 text-[11px] text-ink-faint" title="계약 차주는 다르고 회차만 이 차주에게 넘어온 계약">(승계)</span>}</Td>
                <Td>{dash(r.start_drive_mon)}</Td>
                <Td num>{dash(r.amt_cnt)}{r.month_cnt ? ` / ${String(r.month_cnt)}` : ''}</Td>
                <Td num>{dash(r.left_cnt)}</Td><Td num>{money(r.left_sum)}</Td><Td num>{nz(r.this_amt) || '—'}</Td>
                <Td num>{money(r.month_amount)}</Td><Td num>{money(r.pr_amount)}</Td><Td>{day(r.repay_date)}</Td>
                <Td clip wide>{dash(r.comment)}</Td>
              </tr>
            );
          })}
        </Grid>
      </div>
      {lines.length > 0 && (
        <div className="min-h-0 flex-1 border-t border-line">
          <Grid head={['회차', '정산월', '금액', '차주', '비고']}
                foot={<tr><Td colSpan={2} strong>할부 총합계 {lines.length}회</Td><Td num strong>{money(sumOf(lines, 'amt'))}</Td><Td colSpan={2} /></tr>}>
            {lines.map((a) => (
              <tr key={String(a.fin_inst_amt_id)} className={String(a.drive_mon) === res.mon ? 'bg-amber-50' : ''}>
                <Td num>{dash(a.sort)}</Td><Td>{dash(a.drive_mon)}</Td><Td num>{money(a.amt)}</Td>
                <Td>{dash(a.owner_name)}</Td><Td clip wide>{dash(a.comment)}</Td>
              </tr>
            ))}
          </Grid>
        </div>
      )}
    </div>
  );
}

/** 탭 안 작은 표 — 머리글 고정(`.fit-box`) · 상자 안만 스크롤. */
function Grid({ head, children, foot, note, empty }: {
  head: string[]; children: ReactNode; foot?: ReactNode; note?: string | null; empty?: string;
}) {
  const none = Array.isArray(children) && children.length === 0;
  return (
    <div className="fit-box h-full overflow-auto">
      {note && <p className="px-2 py-0.5 text-[11px] text-ink-faint">{note}</p>}
      {none && empty ? <p className="p-3 text-xs text-ink-faint">{empty}</p> : (
        <table className="min-w-full text-xs">
          <thead><tr>{head.map((h) => <th key={h} className="th px-2 py-1">{h}</th>)}</tr></thead>
          <tbody>{children}</tbody>
          {foot && <tfoot className="sticky bottom-0 bg-canvas">{foot}</tfoot>}
        </table>
      )}
    </div>
  );
}

function Td({ children, num, clip, wide, strong, colSpan }: {
  children?: ReactNode; num?: boolean; clip?: boolean; wide?: boolean; strong?: boolean; colSpan?: number;
}) {
  return (
    <td colSpan={colSpan}
        className={`whitespace-nowrap border-b border-line px-2 py-1 ${num ? 'num' : ''} ${strong ? 'font-semibold' : ''} ${
          clip ? `${wide ? 'max-w-[20rem]' : 'max-w-[12rem]'} overflow-hidden text-ellipsis` : ''}`}
        title={clip && typeof children === 'string' ? children : undefined}>
      {children}
    </td>
  );
}
